/**
 * Boot-shell handoff and last-resort error reporting.
 *
 * The inline `#boot` element in index.html paints on the first frame. This
 * module is responsible for taking it down once React is actually on screen,
 * and for making sure that a failure *before* React mounts still produces
 * something a person can read and act on — rather than the blank white screen
 * or Safari "cannot open the page" that the previous build fell back to.
 */

const BOOT_ID = 'boot'

declare global {
  interface Window {
    __bootSlowTimer?: ReturnType<typeof setTimeout>
  }
}

/** Remove the inline boot shell. Safe to call more than once. */
export function dismissBootShell(): void {
  if (window.__bootSlowTimer !== undefined) {
    clearTimeout(window.__bootSlowTimer)
    delete window.__bootSlowTimer
  }
  document.getElementById(BOOT_ID)?.remove()
}

/**
 * Replace the boot shell with a readable failure state.
 *
 * Used only for faults that happen before React can render its own error
 * boundary (module load failure, a throw inside `createRoot`). Deliberately
 * plain DOM: if React never started, we cannot rely on it here.
 */
export function renderFatalBootError(detail: string): void {
  if (window.__bootSlowTimer !== undefined) {
    clearTimeout(window.__bootSlowTimer)
    delete window.__bootSlowTimer
  }

  const boot = document.getElementById(BOOT_ID)
  const host = boot ?? document.body.appendChild(document.createElement('div'))
  host.id = BOOT_ID
  host.className = ''
  host.setAttribute('role', 'alert')

  host.replaceChildren()

  const wrap = document.createElement('div')
  wrap.style.cssText =
    'position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;' +
    'justify-content:center;gap:1rem;padding:2rem;text-align:center;' +
    'font-family:-apple-system,BlinkMacSystemFont,system-ui,sans-serif;'

  const title = document.createElement('h1')
  title.textContent = "ScanFlow couldn't start"
  title.style.cssText = 'font-size:1.25rem;margin:0;color:#0f172a;'

  const body = document.createElement('p')
  body.textContent =
    'Something went wrong while loading the app. Reloading usually fixes it. ' +
    'If it keeps happening, use Reset app data.'
  body.style.cssText = 'font-size:0.9375rem;line-height:1.5;margin:0;max-width:24rem;color:#475569;'

  const code = document.createElement('code')
  code.textContent = detail.slice(0, 300)
  code.style.cssText =
    'font-size:0.75rem;color:#be123c;background:#fff1f2;padding:0.5rem 0.75rem;' +
    'border-radius:0.5rem;max-width:24rem;overflow-wrap:anywhere;'

  const reload = document.createElement('button')
  reload.textContent = 'Reload'
  reload.style.cssText =
    'margin-top:0.5rem;min-height:2.75rem;padding:0 2rem;border:0;border-radius:0.875rem;' +
    'background:#2563eb;color:#fff;font-size:0.9375rem;font-weight:600;'
  reload.onclick = () => window.location.reload()

  const reset = document.createElement('button')
  reset.textContent = 'Reset app data'
  reset.style.cssText =
    'min-height:2.75rem;padding:0 1rem;border:0;background:none;color:#64748b;font-size:0.875rem;'
  reset.onclick = () => {
    void hardReset()
  }

  wrap.append(title, body, code, reload, reset)
  host.append(wrap)
}

/**
 * Clear every client-side cache and reload.
 *
 * The escape hatch for a device stuck on a bad service worker or corrupt
 * cached state — the situation that previously required deleting and
 * re-adding the Home Screen app.
 */
export async function hardReset(): Promise<void> {
  try {
    const registrations = await navigator.serviceWorker?.getRegistrations?.()
    await Promise.all((registrations ?? []).map((r) => r.unregister()))
  } catch {
    /* best effort */
  }

  try {
    const keys = await caches.keys()
    await Promise.all(keys.map((k) => caches.delete(k)))
  } catch {
    /* best effort */
  }

  try {
    localStorage.clear()
    sessionStorage.clear()
  } catch {
    /* best effort */
  }

  // Cache-bust so we don't come straight back up on the same bad bundle.
  window.location.replace(`${window.location.origin}/?reset=${Date.now().toString(36)}`)
}
